import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, Filter, X } from 'lucide-react';
import Header from './Header';
import MovieRow from './MovieRow';
import MovieModal from './MovieModal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  
  const [searchQuery, setSearchQuery] = useState(query);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [contentFilter, setContentFilter] = useState('all'); // 'all', 'video', 'audio', 'image'

  useEffect(() => {
    if (query) {
      performSearch(query);
    }
  }, [query]);

  const performSearch = async (searchTerm) => {
    if (!searchTerm.trim()) return;
    
    setLoading(true);
    try {
      const params = {
        query: searchTerm,
        limit: 50
      };
      
      if (contentFilter !== 'all') {
        params.content_type = contentFilter;
      }
      
      const response = await axios.get(`${API_URL}/api/search`, { params });
      
      // Transform backend content to movie format
      const transformedResults = response.data.results.map(content => ({
        id: content.id,
        title: content.title,
        description: content.description || 'No description available',
        poster: content.thumbnail_url || 'https://via.placeholder.com/500x750?text=No+Image',
        backdrop: content.thumbnail_url || 'https://via.placeholder.com/1920x1080?text=No+Image',
        videoId: content.file_path || content.id,
        genre: content.genre || 'General',
        year: new Date(content.created_at).getFullYear(),
        rating: 'Not Rated',
        duration: content.duration || 'N/A',
        isTrending: content.view_count > 100,
        realContent: true,
        contentType: content.content_type,
        streamUrl: content.content_type === 'video' ? `${API_URL}/api/stream/${content.id}/master.m3u8` : null
      }));
      
      setResults(transformedResults);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      performSearch(searchQuery);
    }
  };

  const handlePlay = (movie) => {
    setSelectedMovie(movie);
    setIsModalOpen(true);
  };

  const handleOpenDetails = (movie) => {
    setSelectedMovie(movie);
    setIsModalOpen(true);
  };

  const handleAddToList = (movie) => {
    console.log('Added to list:', movie.title);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMovie(null);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setResults([]);
    navigate('/browse');
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      <div className="pt-24 px-8 pb-8">
        {/* Search Bar */}
        <div className="max-w-4xl mx-auto mb-8">
          <form onSubmit={handleSearch} className="relative">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for movies, shows, music, creators..."
                  className="w-full pl-12 pr-12 py-6 bg-gray-900 border-gray-700 text-white text-lg focus:border-purple-500"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 px-8 text-lg"
              >
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </form>

          {/* Filters */}
          <div className="flex gap-3 mt-4">
            <Button
              variant={contentFilter === 'all' ? 'default' : 'outline'}
              onClick={() => setContentFilter('all')}
              className={contentFilter === 'all' ? 'bg-purple-600' : 'border-gray-600 text-gray-300'}
            >
              All
            </Button>
            <Button
              variant={contentFilter === 'video' ? 'default' : 'outline'}
              onClick={() => setContentFilter('video')}
              className={contentFilter === 'video' ? 'bg-purple-600' : 'border-gray-600 text-gray-300'}
            >
              Videos
            </Button>
            <Button
              variant={contentFilter === 'audio' ? 'default' : 'outline'}
              onClick={() => setContentFilter('audio')}
              className={contentFilter === 'audio' ? 'bg-purple-600' : 'border-gray-600 text-gray-300'}
            >
              Audio
            </Button>
            <Button
              variant={contentFilter === 'image' ? 'default' : 'outline'}
              onClick={() => setContentFilter('image')}
              className={contentFilter === 'image' ? 'bg-purple-600' : 'border-gray-600 text-gray-300'}
            >
              Images
            </Button>
          </div>
        </div>

        {/* Search Results */}
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
              <p className="text-white mt-4">Searching...</p>
            </div>
          ) : query && results.length > 0 ? (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-white mb-2">
                  Search Results for "{query}"
                </h2>
                <p className="text-gray-400">{results.length} results found</p>
              </div>
              
              <MovieRow
                title="Search Results"
                movies={results}
                onPlay={handlePlay}
                onAddToList={handleAddToList}
                onOpenDetails={handleOpenDetails}
                size="normal"
              />
            </>
          ) : query && results.length === 0 ? (
            <div className="text-center py-20">
              <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">No results found</h3>
              <p className="text-gray-400 mb-6">
                We couldn't find anything for "{query}". Try different keywords.
              </p>
              <Button
                onClick={clearSearch}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                Back to Browse
              </Button>
            </div>
          ) : (
            <div className="text-center py-20">
              <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Start Searching</h3>
              <p className="text-gray-400">
                Search for movies, shows, music, and more...
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Movie Modal */}
      {selectedMovie && (
        <MovieModal
          movie={selectedMovie}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onPlay={handlePlay}
          onAddToList={handleAddToList}
        />
      )}
    </div>
  );
};

export default SearchResults;
